import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  Radio,
  Stack,
  Typography,
} from "@mui/material";
import he from "he";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import enterprisePlanIcon from "../../../assets/icons/enterprisePlanIcon.svg";
import premiumPlanIcon from "../../../assets/icons/premiumPlanIcon.svg";
import recommendedPlan from "../../../assets/icons/recommendedPlan.svg";
import standardPlanIcon from "../../../assets/icons/standardPlanIcon.svg";
import tickMarkIcon from "../../../assets/icons/tickMarkIcon.svg";
import { CustomDialog } from "../../../global/components";
import urls from "../../../global/constants/UrlConstants";
import { handleErrorMessage } from "../../../helpers/methods";
import { AppPlan, Prices, SelectedPlanState } from "../../../models/interfaces";
import history from "../../../utils/history";
import {
  LightTooltip,
  centerItemFlex,
  regularFont,
} from "../../../utils/styles";
import PromoCodeDialog from "../../Shared/Dialogs/PromoCodeDialog/PromoCodeDialog";
import pricingStyles from "../Pricing.styles";
import { getAppPlans } from "../PricingService";

const PRICING_INFO_SEEN_KEY = "pricingInfoSeen";

interface CustomProps {
  setIsLoading: Function;
  change?: boolean;
}

type CurrencyCode = "USD" | "PLN";
type BillingType = "recurring" | "one_time";

const Plans = (props: CustomProps) => {
  const classes = pricingStyles;
  const { t, i18n } = useTranslation();
  const [plans, setPlans] = useState<AppPlan[]>([]);
  const [promo, setPromo] = useState<AppPlan | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<SelectedPlanState>();
  const [openPromo, setOpenPromo] = useState<boolean>(false);
  const [selectedCurrency, setSelectedCurrency] = useState<CurrencyCode>("PLN");
  const [billingType, setBillingType] = useState<BillingType>("recurring");
  const [openPricingInfoDialog, setOpenPricingInfoDialog] = useState(true);
  const [recurringConsent, setRecurringConsent] = useState(false);

  const planImage: any = {
    Basic: standardPlanIcon,
    Premium: premiumPlanIcon,
    Lifetime: enterprisePlanIcon,
    Enterprise: enterprisePlanIcon,
  };

  useEffect(() => {
    getPlansList();
  }, [i18n.language]);

  const getFilteredPrices = (
    plan: AppPlan | null,
    isPromo: boolean,
  ): Prices[] => {
    if (!plan?.prices?.length) return [];
    const byCurrency = plan.prices.filter(
      (p) => (p.currency ?? "USD").toUpperCase() === selectedCurrency,
    );
    const planName = plan.name?.toLowerCase();
    const isBasic = planName === "basic";
    const isPremium = planName === "premium";
    const applyBillingFilter = (isBasic || isPremium) && !isPromo;
    if (!applyBillingFilter) return byCurrency;
    const byBilling = byCurrency.filter(
      (p) => (p.billingType ?? "recurring") === billingType,
    );
    // When selected billing type has no prices for this currency, show all prices for the currency
    if (byBilling.length === 0) return byCurrency;
    return byBilling;
  };

  const getPriceSymbol = (currency: string | undefined) => {
    const c = (currency ?? "USD").toUpperCase();
    return c === "PLN" ? "zł" : "$";
  };

  /** Decode HTML entities (e.g. &#322; → ł) and format as "symbol amount" */
  const formatPriceDisplay = (
    rawPrice: string | undefined,
    currency: string | undefined,
  ): string => {
    if (rawPrice == null || rawPrice === "")
      return getPriceSymbol(currency) + " 0";
    const decoded = he.decode(rawPrice);
    const symbol = getPriceSymbol(currency);
    const amount = decoded
      .replace(/\$|&#x24;|&\#36;|&dollar;|zł|ł|PLN|&#322;|&#x13a;/gi, "")
      .trim();
    return amount ? `${symbol} ${amount}` : `${symbol} 0`;
  };

  useEffect(() => {
    setRecurringConsent(false);
  }, [selectedPlan?.priceId]);

  useEffect(() => {
    if (!selectedPlan?.priceId || !selectedPlan.plan?.prices) return;
    const filtered = getFilteredPrices(
      selectedPlan.plan,
      selectedPlan.plan === promo,
    );
    const stillValid = filtered.some((p) => p.priceId === selectedPlan.priceId);
    if (!stillValid) setSelectedPlan(undefined);
  }, [selectedCurrency, billingType]);

  const getPlansList = async () => {
    try {
      props.setIsLoading(true);
      const language = i18n.language.toLowerCase();
      const response = await getAppPlans(language);
      setPlans(response.plans);
      setPromo(response.promo);
      props.setIsLoading(false);
    } catch (error: any) {
      props.setIsLoading(false);
      handleErrorMessage(error);
    }
  };

  const getPromoCard = () => {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", flexGrow: 1 }}>
        <Box
          sx={
            selectedPlan?.plan === promo
              ? {
                  ...classes.priceBox,
                  border: "1px solid #8876F0",
                  display: "flex",
                  flexDirection: "column",
                  flexGrow: 1,
                  mt: 4,
                }
              : {
                  ...classes.priceBox,
                  display: "flex",
                  flexDirection: "column",
                  flexGrow: 1,
                  mt: 4,
                }
          }
        >
          <Stack
            direction={"column"}
            alignItems={"center"}
            spacing={2}
            sx={{
              height: "100%",
              justifyContent: "space-between",
              flex: 1,
            }}
          >
            <Box>
              <Stack direction={"column"} alignItems={"center"}>
                <img
                  src={standardPlanIcon}
                  alt="Module Not Found..."
                  height={"80px"}
                />
                <Typography sx={classes.planText} py={{ sm: 2, xs: 1 }}>
                  {promo?.name}
                </Typography>{" "}
                <Divider
                  sx={{
                    border: "1px solid rgba(255, 255, 255, 0.05)",
                    width: "100%",
                  }}
                />
                {(() => {
                  const promoPrices = getFilteredPrices(promo, true);
                  if (promoPrices.length === 0 || !promo) return null;
                  return (
                    <Box sx={{ width: "100%", py: 2 }}>
                      <Stack
                        direction="row"
                        spacing={2}
                        justifyContent="center"
                      >
                        <Grid
                          container
                          spacing={1}
                          justifyContent="center"
                          gap={"15px"}
                        >
                          {promoPrices.map((price) => {
                            const isSelected =
                              selectedPlan?.plan === promo &&
                              selectedPlan?.priceId === price.priceId;
                            return (
                              <Grid
                                item
                                key={price.priceId}
                                xs={12}
                                sm={12}
                                md={6}
                                lg={6}
                                xl={6}
                              >
                                <Box
                                  sx={
                                    isSelected
                                      ? {
                                          ...classes.priceRadioBox,
                                          ...classes.selectedPriceRadioBox,
                                        }
                                      : {
                                          ...classes.priceRadioBox,
                                        }
                                  }
                                  onClick={() =>
                                    setSelectedPlan({
                                      name: promo.name!,
                                      duration: price.duration!,
                                      price: price.effectivePrice!,
                                      priceId: price.priceId!,
                                      details: promo.details!,
                                      plan: promo,
                                      currency: price.currency!,
                                      billingType: price.billingType!,
                                    })
                                  }
                                >
                                  <Radio
                                    checked={isSelected}
                                    value={price.priceId}
                                    onChange={() =>
                                      setSelectedPlan({
                                        plan: promo,
                                        name: promo.name!,
                                        duration: price.duration!,
                                        price: price.effectivePrice!,
                                        priceId: price.priceId!,
                                        details: promo.details!,
                                        currency: price.currency!,
                                        billingType: price.billingType!,
                                      })
                                    }
                                    sx={
                                      isSelected
                                        ? {
                                            ...classes.priceRadioButton,
                                          }
                                        : classes.priceRadioButton
                                    }
                                  />
                                  <Stack direction={"column"}>
                                    <Typography sx={classes.planDescText}>
                                      7 {price.duration}s
                                    </Typography>
                                    <Typography sx={classes.priceText}>
                                      {formatPriceDisplay(
                                        "0",
                                        selectedCurrency,
                                      )}
                                    </Typography>
                                  </Stack>
                                </Box>
                              </Grid>
                            );
                          })}
                        </Grid>
                      </Stack>
                    </Box>
                  );
                })()}
                <Typography sx={classes.planDescText} pb={{ sm: 4, xs: 2 }}>
                  {promo?.details}
                </Typography>
                <Grid container pb={4} sx={{ px: "5%" }}>
                  {promo?.keyFeatures?.map((point: string) => {
                    return (
                      <Grid item p={1.5}>
                        <Stack
                          direction={"row"}
                          alignItems={"center"}
                          spacing={1}
                        >
                          <img
                            src={tickMarkIcon}
                            alt="Module Not Found..."
                            height={"12px"}
                          />
                          <Typography
                            sx={{
                              ...classes.planDescText,
                              px: 0,
                              textTransform: "capitalize",
                            }}
                          >
                            {point}
                          </Typography>
                        </Stack>
                      </Grid>
                    );
                  })}
                </Grid>
              </Stack>
            </Box>
          </Stack>
        </Box>
      </Box>
    );
  };

  const getCards = () => {
    return (
      <Grid
        container
        justifyContent={"center"}
        spacing={2}
        sx={{ alignItems: "stretch" }}
      >
        <Grid
          item
          xl={4}
          lg={6}
          md={10}
          sm={12}
          xs={12}
          sx={{ display: "flex", flexDirection: "column" }}
        >
          {getPromoCard()}
        </Grid>
        {plans?.map((plan: AppPlan, index: number) => {
          return (
            <Grid
              item
              xl={4}
              lg={6}
              md={10}
              sm={12}
              xs={12}
              sx={{ display: "flex", flexDirection: "column" }}
              key={index}
            >
              <Box
                sx={{ display: "flex", flexDirection: "column", flexGrow: 1 }}
              >
                {plan.isRecommended && (
                  <>
                    <Typography sx={classes.recommendsText}>
                      ★ {t("recommends")}
                    </Typography>
                    <Box sx={classes.recommendedPlan}>
                      <img
                        src={recommendedPlan}
                        alt="Module Not Found..."
                        style={{ position: "relative", top: "2px" }}
                        height={"40px"}
                      />
                    </Box>
                  </>
                )}

                <Box
                  sx={
                    selectedPlan?.plan === plan
                      ? {
                          ...classes.priceBox,
                          border: "1px solid #8876F0",
                          display: "flex",
                          flexDirection: "column",
                          flexGrow: 1,
                          mt: !plan.isRecommended ? 4 : 0,
                        }
                      : {
                          ...classes.priceBox,
                          display: "flex",
                          flexDirection: "column",
                          flexGrow: 1,
                          mt: !plan.isRecommended ? 4 : 0,
                        }
                  }
                >
                  <Stack
                    direction={"column"}
                    alignItems={"center"}
                    spacing={2}
                    sx={{
                      height: "100%",
                      justifyContent: "space-between",
                      flex: 1,
                    }}
                  >
                    <Box>
                      <Stack direction={"column"} alignItems={"center"}>
                        <img
                          src={planImage[plan.name ?? "Basic"]}
                          alt="Module Not Found..."
                          height={"80px"}
                        />
                        <Typography sx={classes.planText} py={{ sm: 2, xs: 1 }}>
                          {plan.name}
                        </Typography>{" "}
                        <Divider
                          sx={{
                            border: "1px solid rgba(255, 255, 255, 0.05)",
                            width: "100%",
                          }}
                        />
                        {planHasBillingChoice(plan) && (
                          <Stack
                            direction="column"
                            alignItems="center"
                            sx={{ width: "100%", pt: 1.5 }}
                            spacing={1}
                          >
                            <Box sx={classes.billingSegmentedGroup}>
                              {(["recurring", "one_time"] as BillingType[]).map(
                                (type) => {
                                  const isRecurring = type === "recurring";
                                  const label = isRecurring
                                    ? t("subscription") || "Recurring"
                                    : t("oneTime") || "One-time";
                                  const tooltipTitle = isRecurring
                                    ? t("billingRecurringDesc") ||
                                      "Billed at each period; renews automatically until you cancel."
                                    : t("billingOneTimeDesc") ||
                                      "Single payment; access for the chosen duration, no automatic renewal.";
                                  return (
                                    <LightTooltip
                                      key={type}
                                      title={tooltipTitle}
                                      placement="top"
                                      arrow
                                      componentsProps={{
                                        tooltip: {
                                          sx: {
                                            maxWidth: 280,
                                            textAlign: "center",
                                            fontSize: "18px !important",
                                          },
                                        },
                                      }}
                                    >
                                      <Box
                                        component="button"
                                        type="button"
                                        sx={
                                          billingType === type
                                            ? {
                                                ...classes.billingOption,
                                                ...classes.billingOptionSelected,
                                              }
                                            : classes.billingOption
                                        }
                                        onClick={() => setBillingType(type)}
                                      >
                                        {label}
                                      </Box>
                                    </LightTooltip>
                                  );
                                },
                              )}
                            </Box>
                          </Stack>
                        )}
                        {(() => {
                          const planPrices = getFilteredPrices(plan, false);
                          return (
                            planPrices.length > 0 && (
                              <Box sx={{ width: "100%", py: 2 }}>
                                <Stack
                                  direction="row"
                                  spacing={2}
                                  justifyContent="center"
                                >
                                  <Grid
                                    container
                                    spacing={1}
                                    justifyContent="center"
                                  >
                                    {planPrices.map((price) => {
                                      const isSelected =
                                        selectedPlan?.plan === plan &&
                                        selectedPlan?.priceId === price.priceId;
                                      const hasDiscount =
                                        price.discount &&
                                        price.discount !== "0" &&
                                        price.discount !== "0%";
                                      return (
                                        <Grid
                                          item
                                          key={price.priceId}
                                          xs={12}
                                          sm={12}
                                          md={6}
                                          lg={6}
                                          xl={6}
                                        >
                                          <Box
                                            sx={
                                              isSelected
                                                ? {
                                                    ...classes.priceRadioBox,
                                                    ...classes.selectedPriceRadioBox,
                                                    border: hasDiscount
                                                      ? "2px solid #FF5252"
                                                      : "2px solid #9053F6",
                                                  }
                                                : {
                                                    ...classes.priceRadioBox,
                                                    border: hasDiscount
                                                      ? "2px solid #FF5252"
                                                      : "2px solid rgba(255,255,255,0.18)",
                                                  }
                                            }
                                            onClick={() =>
                                              setSelectedPlan({
                                                name: plan.name!,
                                                duration: price.duration!,
                                                price: price.effectivePrice!,
                                                priceId: price.priceId!,
                                                details: plan.details!,
                                                plan,
                                                currency: price.currency!,
                                                billingType: price.billingType!,
                                              })
                                            }
                                          >
                                            {hasDiscount && (
                                              <Box sx={classes.discountBadge}>
                                                {t("save")} {price.discount}%
                                              </Box>
                                            )}
                                            <Radio
                                              checked={isSelected}
                                              value={price.priceId}
                                              onChange={() =>
                                                setSelectedPlan({
                                                  plan,
                                                  name: plan.name!,
                                                  duration: price.duration!,
                                                  price: price.effectivePrice!,
                                                  priceId: price.priceId!,
                                                  details: plan.details!,
                                                  currency: price.currency!,
                                                  billingType:
                                                    price.billingType!,
                                                })
                                              }
                                              sx={
                                                isSelected
                                                  ? {
                                                      ...classes.priceRadioButton,
                                                      "&.Mui-checked": {
                                                        color: hasDiscount
                                                          ? "#FF5252"
                                                          : "#9053F6",
                                                      },
                                                    }
                                                  : classes.priceRadioButton
                                              }
                                            />
                                            <Stack direction={"column"}>
                                              <Typography
                                                sx={classes.planDescText}
                                              >
                                                1 {price.duration}
                                              </Typography>
                                              <Stack direction={"row"}>
                                                <Typography
                                                  sx={classes.priceText}
                                                >
                                                  {formatPriceDisplay(
                                                    price?.effectivePrice,
                                                    price.currency,
                                                  )}
                                                </Typography>
                                                {hasDiscount && (
                                                  <Typography
                                                    sx={classes.realPriceText}
                                                    pl={1}
                                                  >
                                                    {formatPriceDisplay(
                                                      price?.price,
                                                      price.currency,
                                                    )}
                                                  </Typography>
                                                )}
                                              </Stack>
                                            </Stack>
                                          </Box>
                                        </Grid>
                                      );
                                    })}
                                  </Grid>
                                </Stack>
                              </Box>
                            )
                          );
                        })()}
                        <Typography
                          sx={classes.planDescText}
                          pb={{ sm: 4, xs: 2 }}
                        >
                          {plan.details}
                        </Typography>
                        <Grid container pb={4} px={{ sm: "5%", xs: 0 }}>
                          {plan?.keyFeatures?.map((point: string) => {
                            return (
                              <Grid item p={{ sm: 1.5, xs: 1 }}>
                                <Stack
                                  direction={"row"}
                                  alignItems={"center"}
                                  spacing={1}
                                >
                                  <img
                                    src={tickMarkIcon}
                                    alt="Module Not Found..."
                                    height={"12px"}
                                  />
                                  <Typography
                                    sx={{
                                      ...classes.planDescText,
                                      px: 0,
                                      textTransform: "capitalize",
                                    }}
                                  >
                                    {point}
                                  </Typography>
                                </Stack>
                              </Grid>
                            );
                          })}
                        </Grid>
                      </Stack>
                    </Box>
                  </Stack>
                </Box>
              </Box>
            </Grid>
          );
        })}
      </Grid>
    );
  };

  const getSubscriptionBtn = () => {
    const isRecurring = selectedPlan?.billingType === "recurring";
    const isDisabled = !selectedPlan || (isRecurring && !recurringConsent);
    return (
      <Box
        marginTop={"50px"}
        width={"100%"}
        display={"flex"}
        flexDirection={"column"}
        alignItems={"center"}
        gap={2}
      >
        {selectedPlan && isRecurring && (
          <FormControlLabel
            control={
              <Checkbox
                checked={recurringConsent}
                onChange={(e) => setRecurringConsent(e.target.checked)}
                sx={{
                  color: "rgba(255,255,255,0.5)",
                  "&.Mui-checked": { color: "#9053F6" },
                  pt: 0,
                  alignSelf: "flex-start",
                }}
              />
            }
            label={
              <Typography
                sx={{
                  color: "rgba(255,255,255,0.75)",
                  fontSize: "18px",
                  lineHeight: 1.5,
                  ...regularFont,
                  maxWidth: "700px",
                }}
              >
                {t("recurringConsentLabel")}
              </Typography>
            }
            sx={{
              alignItems: "flex-start",
              mx: 0,
              textAlign: "left",
              maxWidth: "700px",
            }}
          />
        )}
        <Button
          sx={classes.subscribeBtn}
          onClick={() => {
            selectedPlan?.priceId
              ? history.push({
                  pathname: urls.paymentViewPath,
                  state: {
                    selectedPlan,
                    change: props.change,
                  },
                })
              : setOpenPromo(true);
          }}
          disabled={isDisabled}
        >
          <Typography sx={classes.subscribeBtnTypo}>
            {t("subscribeHeader")}
          </Typography>
        </Button>
      </Box>
    );
  };

  const planHasBillingChoice = (plan: AppPlan) => {
    const planName = plan.name?.toLowerCase();
    const isBasicOrPremium = planName === "basic" || planName === "premium";
    if (!isBasicOrPremium || !plan.prices?.length) return false;
    const byCurrency = plan.prices.filter(
      (pr) => (pr.currency ?? "USD").toUpperCase() === selectedCurrency,
    );
    const hasOneTime = byCurrency.some((pr) => pr.billingType === "one_time");
    const hasRecurring = byCurrency.some(
      (pr) => pr.billingType === "recurring" || !pr.billingType,
    );
    return hasOneTime && hasRecurring;
  };

  const getCurrencySelector = (large = false) => {
    const groupSx = large
      ? classes.segmentedGroupLarge
      : classes.segmentedGroup;
    const optionSx = large
      ? classes.currencyOptionLarge
      : classes.currencyOption;
    const selectedSx = large
      ? classes.currencyOptionLargeSelected
      : classes.currencyOptionSelected;
    return (
      <Box sx={groupSx}>
        {(["PLN", "USD"] as CurrencyCode[]).map((code) => (
          <Box
            key={code}
            component="button"
            type="button"
            sx={
              selectedCurrency === code
                ? { ...optionSx, ...selectedSx }
                : optionSx
            }
            onClick={() => setSelectedCurrency(code)}
          >
            {code === "USD" ? "USD ($)" : "PLN (zł)"}
          </Box>
        ))}
      </Box>
    );
  };

  const getHeader = () => {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          flexWrap: "nowrap",
          pb: 4,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }} />
        <Stack
          direction="column"
          alignItems="center"
          justifyContent="center"
          spacing={0}
          sx={{ flex: 1, minWidth: 0 }}
        >
          <Typography sx={classes.italicTypo}>
            {t("subscribeHeader")}
          </Typography>
          {getCurrencySelector(true)}
        </Stack>
        <Box sx={{ flex: 1, minWidth: 0 }} />
      </Box>
    );
  };

  const closePricingInfoDialog = () => {
    setOpenPricingInfoDialog(false);
  };

  const getPricingInfoDialogHeader = () => (
    <Stack
      direction="column"
      alignItems="center"
      spacing={2}
      pt={5}
      sx={{
        background:
          "linear-gradient(180deg, #ffffff 0%, rgba(249,250,251,0.98) 100%)",
        pb: 1,
      }}
    >
      <Typography sx={classes.pricingInfoModalHeader}>
        {t("pricingInfoTitle")}
      </Typography>
      <Divider
        sx={{
          border: "none",
          height: 2,
          width: "60%",
          borderRadius: 1,
          background:
            "linear-gradient(90deg, transparent, rgba(144, 83, 246, 0.35), transparent)",
        }}
      />
    </Stack>
  );

  const getPricingInfoDialogBody = () => (
    <Stack
      direction="column"
      pb={5}
      pt={1}
      spacing={2.5}
      sx={{
        px: { sm: 3, xs: 2 },
        maxWidth: 580,
        mx: "auto",
        background:
          "linear-gradient(180deg, rgba(249,250,251,0.5) 0%, rgba(255,255,255,1) 12%)",
        borderRadius: 2,
      }}
    >
      <Typography sx={classes.pricingInfoModalIntro}>
        {t("pricingInfoIntro")}
      </Typography>

      <Box sx={classes.pricingInfoNoteCard}>
        <Typography sx={classes.pricingInfoNoteCardTitle}>
          <Box component="span" sx={classes.pricingInfoNoteCardBadge}>
            1
          </Box>
          {t("pricingInfoCurrencyLabel")}
        </Typography>
        <Typography sx={classes.pricingInfoModalContent}>
          {t("pricingInfoCurrencyNote")}
        </Typography>
      </Box>

      <Box sx={classes.pricingInfoNoteCard}>
        <Typography sx={classes.pricingInfoNoteCardTitle}>
          <Box component="span" sx={classes.pricingInfoNoteCardBadge}>
            2
          </Box>
          {t("pricingInfoPremiumBillingTitle")}
        </Typography>
        <Box sx={classes.pricingInfoBillingRow}>
          <Typography sx={classes.pricingInfoBillingLabel}>
            {t("subscription")}
          </Typography>
          <Typography sx={classes.pricingInfoModalContent}>
            {t("pricingInfoSubscriptionMeaning")}
          </Typography>
        </Box>
        <Box sx={classes.pricingInfoBillingRow}>
          <Typography sx={classes.pricingInfoBillingLabel}>
            {t("oneTime")}
          </Typography>
          <Typography sx={classes.pricingInfoModalContent}>
            {t("pricingInfoOneTimeMeaning")}
          </Typography>
        </Box>
      </Box>

      {/* <Box sx={classes.pricingInfoNoteCard}>
        <Typography sx={classes.pricingInfoNoteCardTitle}>
          <Box component="span" sx={classes.pricingInfoNoteCardBadge}>
            3
          </Box>
          {t("pricingInfoPaymentMethodsLabel")}
        </Typography>
        <Typography sx={classes.pricingInfoModalContent}>
          {t("pricingInfoBlikNotePrefix")}
          <Box component="span" sx={classes.pricingInfoBlikHighlight}>
            {t("pricingInfoBlikNoteHighlight")}
          </Box>
        </Typography>
      </Box> */}

      <Stack direction="row" justifyContent="center" sx={{ pt: 2 }}>
        <Button
          onClick={closePricingInfoDialog}
          sx={classes.pricingInfoGotItBtn}
        >
          {t("gotIt")}
        </Button>
      </Stack>
    </Stack>
  );

  const getPricingInfoDialog = () => (
    <CustomDialog
      width="640px"
      borderRadius="16px"
      isDialogOpen={openPricingInfoDialog}
      closable={true}
      closeButtonVisibility={true}
      handleDialogClose={closePricingInfoDialog}
      dialogHeaderContent={getPricingInfoDialogHeader()}
      dialogBodyContent={getPricingInfoDialogBody()}
    />
  );

  const getPromoCodeDialog = () => {
    return (
      <PromoCodeDialog
        open={openPromo}
        setOpen={setOpenPromo}
        setIsLoading={props.setIsLoading}
      />
    );
  };

  const getPlans = () => {
    return (
      <>
        {getPricingInfoDialog()}
        <Grid container pt={3} sx={{ ...centerItemFlex }}>
          <Grid
            item
            xl={12}
            lg={12}
            md={12}
            sm={12}
            xs={12}
            px={{ sm: 4, xs: 0 }}
          >
            {getHeader()}
            {getCards()}
          </Grid>
        </Grid>
        {getSubscriptionBtn()}
        {getPromoCodeDialog()}
      </>
    );
  };
  return getPlans();
};

export default Plans;
